import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import { useMemo, useState } from "react";
import moment from "moment";
import secureLocalStorage from "react-secure-storage";
import MachineDetailsDropdown from "../dashboard/dropdowns/MachineDetailsDropdown";
import LastFetchedDropdown from "../dashboard/dropdowns/LastFetchedDropdown";
import MachineSyncBar from "../dashboard/MachineSyncBar";
import ProductionValue, {
    DataStatusLegend,
    SyncStatusBadge,
} from "../dashboard/ProductionValue";
import PlansModal from "../dashboard/modals/PlansModal";
import OperatorModal from "../dashboard/modals/OperatorModal";
import FilterModal from "../dashboard/modals/FilterModal";

const SupportDashboard = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const [currentPlan, setCurrentPlan] = useState(null);
    const [currentOperator, setCurrentOperator] = useState(null);
    const [date, setDate] = useState(null);
    const [department, setDepartment] = useState("");
    const { useFetch } = useAPIQuery({
        key: "api/machine-plan",
        endpoint: "/api/machine-plan/",
    });
    const { data, isFetching, refetch } = useFetch({
        params: {
            date: date ?? moment().format("YYYY-MM-DD"),
            department: department ?? "",
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        // Heartbeat goes stale within minutes, so a five minute cache made this screen
        // disagree with Live Site long after a pull had already landed.
        staleTime: 30000,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const modifiedData = useMemo(() => {
        if (data?.results?.data) {
            return data?.results?.data?.map((d) => ({
                ...d,
                version_acc: d?.raw_data?.heartbeat_status?.VERSION,
                is_on_acc: d?.raw_data?.is_on,
                sync_status_acc: d?.raw_data?.sync_status,
                raw_data_acc: isNaN(Number(d?.raw_data?.raw_data))
                    ? 0
                    : Number(d?.raw_data?.raw_data),
                raw_data_morning_acc: isNaN(
                    Number(d?.raw_data?.raw_data_morning)
                )
                    ? 0
                    : Number(d?.raw_data?.raw_data_morning),
                raw_data_night_acc: isNaN(Number(d?.raw_data?.raw_data_night))
                    ? 0
                    : Number(d?.raw_data?.raw_data_night),
                raw_data_cycles_acc: isNaN(Number(d?.raw_data?.raw_data_cycles))
                    ? 0
                    : Number(d?.raw_data?.raw_data_cycles),
                raw_data_morning_cycles_acc: isNaN(
                    Number(d?.raw_data?.raw_data_morning_cycles)
                )
                    ? 0
                    : Number(d?.raw_data?.raw_data_morning_cycles),
                raw_data_night_cycles_acc: isNaN(
                    Number(d?.raw_data?.raw_data_night_cycles)
                )
                    ? 0
                    : Number(d?.raw_data?.raw_data_night_cycles),
                updated_manual_total_acc: isNaN(
                    Number(d?.raw_data?.updated_manual_total)
                )
                    ? 0
                    : Number(d?.raw_data?.updated_manual_total),
                updated_manual_morning_acc: isNaN(
                    Number(d?.raw_data?.updated_manual_morning)
                )
                    ? 0
                    : Number(d?.raw_data?.updated_manual_morning),
                updated_manual_night_acc: isNaN(
                    Number(d?.raw_data?.updated_manual_night)
                )
                    ? 0
                    : Number(d?.raw_data?.updated_manual_night),
            }));
        }
        return [];
    }, [data]);

    const columns = [
        {
            accessor: "machine_name",
            header: "Machine Name",
            filterable: true,
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.machine_name}</span>
                    <MachineDetailsDropdown row={row} />
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_acc",
            header: "Produced Qty",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <ProductionValue row={row} field="raw_data" />
                    {row?.raw_data?.raw_data && (
                        <LastFetchedDropdown row={row} />
                    )}
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_morning_acc",
            header: "Produced Qty Morning",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.raw_data_morning}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_night_acc",
            header: "Produced Qty Night",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.raw_data_night}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_cycles_acc",
            header: "Cycles (shots)",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <ProductionValue row={row} field="raw_data_cycles" />
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_morning_cycles_acc",
            header: "Cycles (shots) Morning",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.raw_data_morning_cycles}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "raw_data_night_cycles_acc",
            header: "Cycles (shots) Night",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.raw_data_night_cycles}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "updated_manual_total_acc",
            header: "Manual Qty Total",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.updated_manual_total}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "updated_manual_morning_acc",
            header: "Manual Qty Morning",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.updated_manual_morning}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "updated_manual_night_acc",
            header: "Manual Qty Night",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span>{row?.raw_data?.updated_manual_night}</span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_raw_count") && {
            accessor: "sync_status_acc",
            header: "Data Status",
            cell: (row) => <SyncStatusBadge row={row} />,
            sortable: true,
            filterable: true,
        },
        privileges?.includes("view_heartbeat") && {
            accessor: "is_on_acc",
            header: "Heartbeat",
            cell: (row) => (
                <div
                    className="d-flex justify-content-between pe-2"
                    style={{ width: "100%" }}
                >
                    <span
                        className={`badge badge-dot bg-${
                            row?.raw_data?.is_on === 0 ? "danger" : "success"
                        }`}
                    >
                        {row?.raw_data?.is_on === 0 ? "Off" : "On"}
                    </span>
                </div>
            ),
            sortable: true,
        },
        privileges?.includes("view_heartbeat_version") && {
            accessor: "version_acc",
            header: "Version",
            cell: (row) => (
                <span>{row?.raw_data?.heartbeat_status?.VERSION ?? "-"}</span>
            ),
            sortable: true,
            filterable: true,
        },
    ].filter(Boolean);
    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            {`Machine details for ${
                                date ?? moment().format("YYYY-MM-DD")
                            } in ${
                                department !== ""
                                    ? department + " department"
                                    : "All departments"
                            }`}
                            {}
                        </h3>
                    </div>
                    <div className="d-flex align-items-center justify-content-center ms-auto">
                        <button
                            className="btn btn-icon btn btn-outline-primary rounded-pill me-3"
                            data-bs-toggle="modal"
                            data-bs-target="#machine-detials-filter-modal"
                        >
                            <em className="icon ni ni-filter-fill" />
                        </button>
                    </div>
                </div>
            </div>
            <MachineSyncBar onSynced={refetch} />
            {privileges?.includes("view_raw_count") && <DataStatusLegend />}
            <Table
                data={modifiedData ?? []}
                columns={columns}
                isLoading={isFetching}
                maxHeight={600}
                manualFilter={false}
                manualSorting={false}
                pagination={false}
                selectRows={false}
                globalSearch={false}
                filterModal={false}
                editRows={false}
                deleteRows={false}
            />
            <PlansModal plan={currentPlan} />
            <OperatorModal operator={currentOperator} />
            <FilterModal setDate={setDate} setDepartment={setDepartment} />
        </>
    );
};

export default SupportDashboard;
